/*
 * Progressive enhancement for sibling Shopify products on the PDP.
 *
 * Related products remain real links. Ordinary same-tab activations fetch the
 * destination's main-product section through Shopify's Section Rendering API,
 * then lets the theme's native Product and Stay AI integrations hydrate it.
 * Ancillary lower-page sections intentionally remain tied to the entry product.
 */
(function pdpProductSwitcher() {
  if (window.welliousPdpProductSwitcherLoaded) return;
  window.welliousPdpProductSwitcherLoaded = true;

  const anchorSelector = 'a[data-pdp-product-switch]';
  const selectSelector = '[data-pdp-product-switcher]';
  const matrixSelector = '[data-pdp-product-matrix]';
  const productRootSelector = 'data-island[data-product-root]';
  const statusSelector = '[data-pdp-switch-status]';
  const supportedTemplateSuffix = 'vanilla';
  const transitionDuration = 520;
  const sectionCache = new Map();
  const pendingRequests = new Map();
  let activeRequest = null;
  let switchSequence = 0;

  function normalizeText(value) {
    return String(value || '')
      .replace(/^subscription:\s*/i, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function productSectionFrom(node) {
    return node ? node.closest('.shopify-section') : null;
  }

  function productRootFrom(section) {
    return section ? section.querySelector(productRootSelector) : null;
  }

  function navigationUrl(value) {
    try {
      const url = new URL(value, window.location.href);
      if (url.origin !== window.location.origin) return null;
      return url;
    } catch (error) {
      return null;
    }
  }

  function cacheKey(url, sectionId) {
    const keyUrl = new URL(url.toString());
    keyUrl.hash = '';
    keyUrl.searchParams.delete('section_id');
    return `${sectionId}:${keyUrl.pathname}${keyUrl.search}`;
  }

  function requestUrl(url, sectionId) {
    const renderedSectionUrl = new URL(url.toString());
    renderedSectionUrl.hash = '';
    renderedSectionUrl.searchParams.set('section_id', sectionId);
    return renderedSectionUrl;
  }

  function sameProductPath(left, right) {
    const leftUrl = navigationUrl(left);
    const rightUrl = navigationUrl(right);
    if (!leftUrl || !rightUrl) return false;
    return leftUrl.pathname.replace(/\/$/, '') === rightUrl.pathname.replace(/\/$/, '');
  }

  function sectionStructure(productRoot) {
    const mainSectionId = String(productRoot?.dataset.productRoot || '');
    if (
      !productRoot ||
      productRoot.dataset.pdpTemplateSuffix !== supportedTemplateSuffix ||
      !mainSectionId.endsWith('__main')
    ) {
      return null;
    }

    return {
      mainSectionId,
      templateSuffix: supportedTemplateSuffix,
    };
  }

  function removeExecutableScripts(section) {
    section.querySelectorAll('script').forEach((script) => {
      const type = normalizeText(script.getAttribute('type'));
      if (type !== 'application/json' && type !== 'application/ld+json') {
        script.remove();
      }
    });
  }

  function parseSectionMarkup(markup, structure, targetUrl, sourceUrl) {
    const sectionId = structure.mainSectionId;
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const section = parsed.getElementById(`shopify-section-${sectionId}`);
    if (!section) throw new Error(`The response omitted section ${sectionId}.`);

    const importedSection = document.importNode(section, true);
    validateMainSection(importedSection, structure, targetUrl, sourceUrl);
    removeExecutableScripts(importedSection);
    return importedSection;
  }

  function validateMainSection(section, structure, targetUrl, sourceUrl) {
    const productRoot = productRootFrom(section);
    const productId = String(productRoot?.dataset.pdpProductId || '');
    const selectedLinks = Array.from(
      section.querySelectorAll(`${anchorSelector}[aria-current='true'][href]`)
    );
    const siblingLinks = Array.from(
      section.querySelectorAll(`${anchorSelector}[href]`)
    );

    if (
      !productRoot ||
      productRoot.dataset.productRoot !== structure.mainSectionId ||
      !String(productRoot.getAttribute('x-data') || '')
        .trim()
        .startsWith('Product(') ||
      productRoot.dataset.pdpTemplateSuffix !== structure.templateSuffix ||
      !/^\d+$/.test(productId) ||
      !selectedLinks.some((link) => sameProductPath(link.href, targetUrl)) ||
      !siblingLinks.some((link) => sameProductPath(link.href, sourceUrl))
    ) {
      throw new Error('The destination is not a compatible reciprocal sibling product.');
    }

    productRoot.removeAttribute('ready');
  }

  async function fetchSectionMarkup(url, structure, sourceUrl, controller) {
    const response = await fetch(requestUrl(url, structure.mainSectionId).toString(), {
      credentials: 'same-origin',
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Product section request failed with status ${response.status}.`);
    }

    const markup = await response.text();
    parseSectionMarkup(markup, structure, url, sourceUrl);
    sectionCache.set(cacheKey(url, structure.mainSectionId), markup);
    return markup;
  }

  function sectionRequest(url, structure, sourceUrl) {
    const key = cacheKey(url, structure.mainSectionId);
    if (sectionCache.has(key)) {
      return {
        controller: null,
        key,
        promise: Promise.resolve(sectionCache.get(key)),
      };
    }

    const pending = pendingRequests.get(key);
    if (pending) return pending;

    const controller = new AbortController();
    const request = { controller, key, promise: null };
    request.promise = fetchSectionMarkup(
      url,
      structure,
      sourceUrl,
      controller
    ).finally(() => {
      if (pendingRequests.get(key) === request) pendingRequests.delete(key);
    });
    pendingRequests.set(key, request);
    return request;
  }

  function parseMatrix(select) {
    const productRoot = select.closest(productRootSelector);
    const matrixElement = productRoot && productRoot.querySelector(matrixSelector);
    if (!matrixElement) return null;

    try {
      return JSON.parse(matrixElement.textContent);
    } catch (error) {
      console.error('[PDP switcher] Could not parse the product matrix.', error);
      return null;
    }
  }

  function initializeSelect(select) {
    if (!select || select.dataset.pdpSwitcherReady === 'true') return;
    const matrix = parseMatrix(select);
    if (!matrix || !matrix.flavors) return;

    const sizeKey = String(select.dataset.sizeKey || '');
    const templateSuffix = String(select.dataset.templateSuffix || '');
    const currentProductId = String(select.dataset.currentProductId || '');
    const options = [];

    if (templateSuffix !== supportedTemplateSuffix) return;

    (matrix.flavorOrder || []).forEach((flavorId) => {
      const flavor = matrix.flavors[flavorId];
      const sibling = flavor && flavor.sizes ? flavor.sizes[sizeKey] : null;
      if (
        !sibling ||
        !sibling.url ||
        String(sibling.templateSuffix || '') !== templateSuffix
      ) {
        return;
      }

      options.push({
        label: flavor.title || sibling.flavorTitle || 'Flavor',
        productId: String(sibling.productId || ''),
        url: sibling.url,
      });
    });

    if (!options.length) return;
    const fragment = document.createDocumentFragment();
    options.forEach((optionData) => {
      const option = document.createElement('option');
      option.value = optionData.url;
      option.textContent = optionData.label;
      option.selected = optionData.productId === currentProductId;
      fragment.appendChild(option);
    });

    select.replaceChildren(fragment);
    select.disabled = options.length < 2;
    select.dataset.pdpSwitcherReady = 'true';
  }

  function initializeSection(section) {
    if (!section) return;
    section.querySelectorAll(selectSelector).forEach(initializeSelect);
  }

  function captureFormState(section) {
    const quantityInput = section.querySelector(
      'form.shopify-product-form [name="quantity"], [name="quantity"][form]'
    );
    const purchaseRadio = section.querySelector(
      '[data-stayai-subscription] input[name="purchaseType"]:checked'
    );
    const frequencySelect = section.querySelector(
      '[data-stayai-subscription] select[name="stayai_selling_plan"]'
    );
    const frequencyOption =
      frequencySelect && frequencySelect.selectedIndex >= 0
        ? frequencySelect.options[frequencySelect.selectedIndex]
        : null;

    return {
      frequencyText: frequencyOption
        ? normalizeText(frequencyOption.textContent)
        : '',
      quantity: quantityInput ? quantityInput.value : '1',
      subscription: Boolean(
        purchaseRadio &&
          /subscription/i.test(`${purchaseRadio.id} ${purchaseRadio.value}`)
      ),
    };
  }

  function validQuantity(input, requestedValue) {
    const minimum = Number(input.min) || 1;
    const maximum = input.max ? Number(input.max) : Infinity;
    const increment = Number(input.step) || 1;
    const requested = Number(requestedValue);
    if (!Number.isFinite(requested)) return minimum;

    const clamped = Math.min(Math.max(requested, minimum), maximum);
    return minimum + Math.floor((clamped - minimum) / increment) * increment;
  }

  function restoreFormState(section, state) {
    if (!section || !state) return;
    section
      .querySelectorAll(
        'form.shopify-product-form [name="quantity"], [name="quantity"][form]'
      )
      .forEach((input) => {
        input.value = String(validQuantity(input, state.quantity));
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

    const purchaseRadios = Array.from(
      section.querySelectorAll(
        '[data-stayai-subscription] input[name="purchaseType"]'
      )
    );
    const subscriptionRadio = purchaseRadios.find((radio) =>
      /subscription/i.test(`${radio.id} ${radio.value}`)
    );
    const oneTimeRadio = purchaseRadios.find((radio) =>
      /one.?time/i.test(`${radio.id} ${radio.value}`)
    );
    const purchaseRadio = state.subscription
      ? subscriptionRadio || oneTimeRadio
      : oneTimeRadio;

    if (purchaseRadio && !purchaseRadio.checked) {
      purchaseRadio.checked = true;
      purchaseRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (state.subscription && subscriptionRadio && state.frequencyText) {
      const frequencySelect = section.querySelector(
        '[data-stayai-subscription] select[name="stayai_selling_plan"]'
      );
      if (frequencySelect) {
        const matchingOption = Array.from(frequencySelect.options).find(
          (option) =>
            option.value &&
            normalizeText(option.textContent) === state.frequencyText
        );
        if (matchingOption && frequencySelect.value !== matchingOption.value) {
          frequencySelect.value = matchingOption.value;
          frequencySelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }

  function focusToken(control) {
    if (control.matches(selectSelector)) return 'select';
    if (control.closest('.product-swatch-row')) return 'size';
    if (control.closest('.kg-product-meta-variant-swatches')) return 'flavor';
    return 'product';
  }

  function restoreFocus(section, token) {
    const selectors = {
      flavor:
        ".kg-product-meta-variant-swatches [data-pdp-product-switch][aria-current='true']",
      product: "[data-pdp-product-switch][aria-current='true']",
      select: selectSelector,
      size: ".product-swatch-row [data-pdp-product-switch][aria-current='true']",
    };
    const target = section.querySelector(selectors[token] || selectors.product);
    if (target && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
  }

  function prepareIncomingMedia(section) {
    section.querySelectorAll(`${productRootSelector} img`).forEach((image) => {
      if (
        image.complete ||
        (!image.getAttribute('src') && !image.getAttribute('srcset'))
      ) {
        return;
      }

      image.dataset.pdpSwitchMedia = 'pending';
      const reveal = () => {
        window.requestAnimationFrame(() => {
          image.dataset.pdpSwitchMedia = 'ready';
          window.setTimeout(() => {
            if (image.isConnected) delete image.dataset.pdpSwitchMedia;
          }, transitionDuration);
        });
      };
      image.addEventListener('load', reveal, { once: true });
      image.addEventListener('error', reveal, { once: true });
    });
  }

  function prepareIncomingSection(section, currentSection) {
    const productRoot = productRootFrom(section);
    if (productRoot) productRoot.setAttribute('on', 'idle');
    section.dataset.pdpSwitchState = 'entering';
    section.style.minHeight = `${Math.ceil(
      currentSection.getBoundingClientRect().height
    )}px`;
    prepareIncomingMedia(section);
  }

  function stickyAtcIsVisible(section) {
    const stickyAtc = section?.querySelector('[data-sticky-atc]');
    return Boolean(
      stickyAtc &&
        stickyAtc.classList.contains('sticky-atc--visible') &&
        stickyAtc.getAttribute('aria-hidden') !== 'true'
    );
  }

  function prepareStickyAtcTransfer(section, shouldTransfer) {
    if (!shouldTransfer) return;
    const stickyAtc = section.querySelector('[data-sticky-atc]');
    if (!stickyAtc) return;
    stickyAtc.classList.add('pdp-sticky-atc-transfer-visible');
    stickyAtc.setAttribute('aria-hidden', 'false');
  }

  function seedStickyAtcFromGeometry(section, shouldTransfer) {
    if (!shouldTransfer) return;
    const productRoot = productRootFrom(section);
    const stickyAtc = section.querySelector('[data-sticky-atc]');
    const trigger = section.querySelector('[data-sticky-atc-trigger]');
    if (!productRoot || !stickyAtc || !trigger) return;

    const header = document.querySelector('[data-sticky-header]');
    const headerThreshold = header
      ? Math.max(header.getBoundingClientRect().bottom, 0)
      : 0;
    const visible = trigger.getBoundingClientRect().bottom <= headerThreshold;
    productRoot.dataset.pdpStickyAtcSeed = visible ? 'visible' : 'hidden';
    stickyAtc.classList.toggle('pdp-sticky-atc-transfer-visible', visible);
    stickyAtc.setAttribute('aria-hidden', String(!visible));
  }

  function revealIncomingSection(section) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!section.isConnected) return;
        section.style.removeProperty('min-height');
        section.dataset.pdpSwitchState = 'ready';
        window.setTimeout(() => {
          if (section.dataset.pdpSwitchState === 'ready') {
            delete section.dataset.pdpSwitchState;
          }
        }, transitionDuration);
      });
    });
  }

  function waitForProductIsland(section) {
    const productRoot = productRootFrom(section);
    if (!productRoot) return Promise.reject(new Error('The destination Product island is missing.'));
    if (productRoot.hasAttribute('ready')) return Promise.resolve();

    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        window.clearTimeout(timeout);
        if (error) reject(error);
        else resolve();
      };
      const observer = new MutationObserver(() => {
        if (productRoot.hasAttribute('ready')) finish();
      });
      const timeout = window.setTimeout(
        () => finish(new Error('The destination Product island did not become ready.')),
        4000
      );
      observer.observe(productRoot, {
        attributes: true,
        attributeFilter: ['ready'],
      });
    });
  }

  async function waitForPurchaseOptions(section) {
    if (!section.querySelector('purchase-options')) return;
    if (!window.customElements?.whenDefined) {
      throw new Error('The destination purchase options lifecycle is unavailable.');
    }

    await new Promise((resolve, reject) => {
      let settled = false;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        if (error) reject(error);
        else resolve();
      };
      const timeout = window.setTimeout(
        () => finish(new Error('The destination purchase options did not become ready.')),
        1500
      );
      window.customElements.whenDefined('purchase-options').then(() => finish());
    });
  }

  function updateUrl(url) {
    const path = `${url.pathname}${url.search}${url.hash}`;
    const currentState =
      window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};
    window.history.replaceState(
      { ...currentState, path },
      '',
      path
    );
  }

  function updatePageTitle(section) {
    const productRoot = productRootFrom(section);
    const pageTitle = productRoot && productRoot.dataset.pdpPageTitle;
    if (pageTitle) document.title = pageTitle;
  }

  function cleanupProductRoot(productRoot) {
    if (typeof productRoot?.welliousProductCleanup === 'function') {
      productRoot.welliousProductCleanup();
    }
  }

  function navigate(url) {
    window.location.assign(url.toString());
  }

  async function switchProduct(control, value, selectedLabel) {
    const targetUrl = navigationUrl(value);
    const currentSection = productSectionFrom(control);
    const currentRoot = productRootFrom(currentSection);
    if (!targetUrl || !currentSection || !currentRoot) {
      if (targetUrl) navigate(targetUrl);
      return;
    }

    const structure = sectionStructure(currentRoot);
    const currentProductId = String(currentRoot.dataset.pdpProductId || '');
    if (!structure || !/^\d+$/.test(currentProductId)) {
      navigate(targetUrl);
      return;
    }
    const sourceUrl = new URL(window.location.href);
    const key = cacheKey(targetUrl, structure.mainSectionId);
    const status = currentSection.querySelector(statusSelector);
    const formState = captureFormState(currentSection);
    const transferStickyAtc = stickyAtcIsVisible(currentSection);
    const token = focusToken(control);
    const sequence = ++switchSequence;

    if (activeRequest && activeRequest.key !== key && activeRequest.controller) {
      activeRequest.controller.abort();
    }

    const request = sectionRequest(targetUrl, structure, sourceUrl);
    activeRequest = request;
    currentSection.dataset.pdpSwitchState = 'loading';
    currentSection.setAttribute('aria-busy', 'true');
    control.setAttribute('aria-busy', 'true');
    if (control instanceof HTMLSelectElement) control.disabled = true;
    if (status) status.textContent = `Loading ${selectedLabel || 'selection'}`;

    try {
      const markup = await request.promise;
      if (sequence !== switchSequence) return;
      const replacementSection = parseSectionMarkup(
        markup,
        structure,
        targetUrl,
        sourceUrl
      );
      prepareIncomingSection(replacementSection, currentSection);
      prepareStickyAtcTransfer(replacementSection, transferStickyAtc);

      document.dispatchEvent(
        new CustomEvent('wellious:product-section:before-replace', {
          detail: {
            section: currentSection,
            targetUrl: targetUrl.toString(),
          },
        })
      );
      cleanupProductRoot(currentRoot);
      currentSection.replaceWith(replacementSection);
      seedStickyAtcFromGeometry(replacementSection, transferStickyAtc);
      initializeSection(replacementSection);

      await waitForProductIsland(replacementSection);
      await waitForPurchaseOptions(replacementSection);
      if (sequence !== switchSequence || !replacementSection.isConnected) return;

      document.dispatchEvent(
        new CustomEvent('wellious:product-section:replaced', {
          detail: {
            section: replacementSection,
            source: 'pdp-product-switcher',
            targetUrl: targetUrl.toString(),
          },
        })
      );
      restoreFormState(replacementSection, formState);
      restoreFocus(replacementSection, token);
      updateUrl(targetUrl);
      updatePageTitle(replacementSection);

      const replacementStatus = replacementSection.querySelector(statusSelector);
      if (replacementStatus) {
        replacementStatus.textContent = `${selectedLabel || 'Product'} selected`;
      }
      replacementSection.removeAttribute('aria-busy');
      revealIncomingSection(replacementSection);

      document.dispatchEvent(
        new CustomEvent('wellious:product-section:ready', {
          detail: {
            section: replacementSection,
            source: 'pdp-product-switcher',
            targetUrl: targetUrl.toString(),
          },
        })
      );
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[PDP switcher] Product switch failed.', error);
      navigate(targetUrl);
    } finally {
      if (activeRequest === request) activeRequest = null;
      if (currentSection.isConnected) {
        delete currentSection.dataset.pdpSwitchState;
        currentSection.removeAttribute('aria-busy');
        control.removeAttribute('aria-busy');
        if (control instanceof HTMLSelectElement) control.disabled = false;
      }
    }
  }

  function prefetchControl(control) {
    if (!control || control.getAttribute('aria-current') === 'true') return;
    const targetUrl = navigationUrl(control.href);
    const section = productSectionFrom(control);
    const productRoot = productRootFrom(section);
    const structure = sectionStructure(productRoot);
    if (!targetUrl || !structure) return;

    sectionRequest(targetUrl, structure, new URL(window.location.href)).promise.catch((error) => {
      if (error.name !== 'AbortError') {
        console.warn('[PDP switcher] Product prefetch failed.', error);
      }
    });
  }

  document.addEventListener('click', (event) => {
    const control =
      event.target instanceof Element ? event.target.closest(anchorSelector) : null;
    if (
      !control ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      control.target === '_blank' ||
      control.hasAttribute('download')
    ) {
      return;
    }

    const targetUrl = navigationUrl(control.href);
    if (!targetUrl) return;
    event.preventDefault();
    if (control.getAttribute('aria-current') === 'true') return;
    switchProduct(
      control,
      targetUrl.toString(),
      control.getAttribute('aria-label') ||
        control.getAttribute('title') ||
        control.textContent.trim()
    );
  });

  document.addEventListener('change', (event) => {
    const select =
      event.target instanceof Element
        ? event.target.closest(selectSelector)
        : null;
    if (!select) return;
    const selectedOption = select.options[select.selectedIndex];
    switchProduct(
      select,
      select.value,
      selectedOption ? selectedOption.textContent.trim() : ''
    );
  });

  ['pointerenter', 'focusin', 'pointerdown'].forEach((eventName) => {
    document.addEventListener(
      eventName,
      (event) => {
        const control =
          event.target instanceof Element
            ? event.target.closest(anchorSelector)
            : null;
        prefetchControl(control);
      },
      eventName === 'pointerenter'
    );
  });

  function initialize() {
    document
      .querySelectorAll(productRootSelector)
      .forEach((root) => initializeSection(productSectionFrom(root)));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
